import { ApiProblem, PASSWORD_CHANGE_REQUIRED, TWO_FACTOR_SETUP_REQUIRED } from './problem';
import type { Grant, Me } from './types';

/**
 * Le client d'API, et le gardien du jeton d'accès (décision D29).
 *
 * LE JETON D'ACCÈS VIT ICI, DANS UNE VARIABLE DE MODULE. Jamais dans
 * `localStorage`, jamais dans `sessionStorage`, jamais dans un cookie lisible.
 * Un script injecté ne peut donc ni le lire au repos, ni lui survivre à un
 * rechargement de page : il ne peut agir que pendant qu'il s'exécute. Le jeton
 * de rafraîchissement, lui, est un cookie `httpOnly` que ce code ne voit pas et
 * n'a pas à voir — le navigateur l'envoie seul, sous `/api/token`.
 */

let apiBaseUrl = '';
let accessToken: string | null = null;
let renewal: Promise<boolean> | null = null;
let preferredLocale: string | null = null;

/** La session est finie : il faut se reconnecter. */
/**
 * D41 — la session est morte en cours d'usage (inactivité, déconnexion
 * ailleurs) : le SessionProvider repasse à la connexion, avec un avis.
 */
export const SESSION_EXPIRED_EVENT = 'kelassi:session-expired';

export class SessionExpired extends Error {
    constructor() {
        super('La session a expiré.');
        this.name = 'SessionExpired';
    }
}

/** Identifiants refusés. Le serveur ne dit pas pourquoi, et c'est voulu. */
export class SignInRefused extends Error {
    constructor() {
        super('Identifiants invalides.');
        this.name = 'SignInRefused';
    }
}

export function configureApi(baseUrl: string): void {
    apiBaseUrl = baseUrl.replace(/\/+$/, '');
}

/**
 * L'adresse complète d'un appel PUBLIC, à lire sans jeton ni cookie (D77 :
 * l'identité de l'établissement, montrée avant la connexion).
 */
export function publicApiUrl(path: string): string {
    return `${apiBaseUrl}${path}`;
}

export function isSignedIn(): boolean {
    return accessToken !== null;
}

/**
 * La langue demandée, envoyée en `Accept-Language`.
 *
 * Le serveur la résout contre les langues que l'établissement a activées et
 * retombe sur sa langue par défaut si elle n'y est pas : le front n'a donc rien
 * à valider, et un code inconnu ne casse rien. `null` rend la main au navigateur.
 */
export function setPreferredLocale(code: string | null): void {
    preferredLocale = code;
}

/**
 * `credentials` est explicite à chaque appel, jamais global.
 *
 * `include` seulement sur `/api/token/*` : c'est la condition pour que le
 * navigateur ENVOIE le cookie de rafraîchissement et ACCEPTE celui que la
 * réponse repose, la requête étant cross-origin. Partout ailleurs, `omit` — le
 * cookie est de toute façon limité au chemin `/api/token`, et le déclarer dit
 * que les appels métier ne s'appuient sur aucune autorisation ambiante : ils
 * portent leur jeton, ou ils échouent.
 */
async function send(path: string, init: RequestInit, withCookie: boolean): Promise<Response> {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');

    if (accessToken !== null) {
        headers.set('Authorization', `Bearer ${accessToken}`);
    }

    if (init.body !== undefined && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    if (preferredLocale !== null) {
        headers.set('Accept-Language', preferredLocale);
    }

    return fetch(`${apiBaseUrl}${path}`, {
        ...init,
        headers,
        credentials: withCookie ? 'include' : 'omit',
    });
}

/**
 * LE POINT LE PLUS IMPORTANT DE CE FICHIER : un seul rafraîchissement à la fois.
 *
 * Un écran qui se monte lance volontiers cinq requêtes en parallèle. Si le jeton
 * vient d'expirer, elles reçoivent cinq 401 — et une implémentation naïve
 * lancerait cinq rafraîchissements avec LA MÊME valeur de cookie.
 *
 * Or le serveur fait tourner le jeton de rafraîchissement : le premier appel le
 * révoque et en délivre un successeur. Les quatre autres présentent alors un
 * jeton déjà révoqué — ce que le serveur interprète, à juste titre, comme un
 * REJEU : chaîne volée, révocation de toute la famille, utilisateur déconnecté,
 * et une ligne `token_reused` au journal de sécurité.
 *
 * Autrement dit, l'implémentation naïve ne gaspille pas des requêtes : elle
 * déconnecte l'utilisateur et déclenche une alerte de sécurité, de façon
 * intermittente et impossible à reproduire. D'où cette promesse partagée.
 */
function renew(): Promise<boolean> {
    renewal ??= (async (): Promise<boolean> => {
        try {
            const response = await send('/api/token/refresh', { method: 'POST' }, true);

            if (!response.ok) {
                accessToken = null;

                return false;
            }

            accessToken = ((await response.json()) as Grant).accessToken;

            return true;
        } catch {
            accessToken = null;

            return false;
        } finally {
            renewal = null;
        }
    })();

    return renewal;
}

/**
 * Un appel métier, rejoué UNE FOIS après un renouvellement.
 *
 * Une seule reprise, jamais de boucle : si le second essai répond encore 401, le
 * problème n'est pas l'expiration du jeton — compte désactivé, droit retiré — et
 * réessayer indéfiniment ne ferait que marteler le serveur.
 *
 * `init.body` est rejoué tel quel : ce client n'accepte donc que des corps
 * rejouables (chaînes, JSON). Un flux ne se relit pas, et le silence sur ce
 * point produirait une seconde requête au corps vide.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const first = await send(path, init, false);

    if (first.status === 403) {
        await signalPasswordChangeRequired(first);
    }

    if (first.status !== 401) {
        return first;
    }

    if (!(await renew())) {
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
        throw new SessionExpired();
    }

    const second = await send(path, init, false);

    if (second.status === 401) {
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
        throw new SessionExpired();
    }

    return second;
}

/**
 * D39 — le mot de passe est juste mais un second facteur est exigé : aucune
 * session encore, seulement un défi à présenter avec le code.
 */
export interface TwoFactorStep {
    readonly challenge: string;
    readonly method: TwoFactorMethod | null;
    readonly methods: readonly TwoFactorMethod[];
    readonly maskedEmail: string;
    readonly resendIn: number;
    readonly mustEnroll: boolean;
    readonly expiresAt: string;
}

export type TwoFactorMethod = 'email' | 'totp' | 'recovery';

/** La session s'ouvre (null), ou un second facteur est demandé. */
export async function signIn(email: string, password: string): Promise<TwoFactorStep | null> {
    const response = await send('/api/token', { method: 'POST', body: JSON.stringify({ email, password }) }, true);

    if (!response.ok) {
        throw new SignInRefused();
    }

    const body = (await response.json()) as Grant | { readonly twoFactor: TwoFactorStep };

    if ('twoFactor' in body) {
        return body.twoFactor;
    }

    accessToken = body.accessToken;

    return null;
}

/** D39 — le code ; la session s'ouvre, ou une ApiProblem dit pourquoi (422 code faux, 401 défi mort). */
export async function verifyTwoFactor(challenge: string, method: TwoFactorMethod, code: string): Promise<void> {
    const response = await send('/api/token/two-factor', { method: 'POST', body: JSON.stringify({ challenge, method, code }) }, true);

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    accessToken = ((await response.json()) as Grant).accessToken;
}

/** D39 — (r)envoie un code par courriel. */
export async function sendTwoFactorEmail(challenge: string): Promise<{ readonly maskedEmail: string; readonly resendIn: number }> {
    const response = await send('/api/token/two-factor/email', { method: 'POST', body: JSON.stringify({ challenge }) }, false);

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return (await response.json()) as { readonly maskedEmail: string; readonly resendIn: number };
}

/**
 * Tente de rouvrir une session sans mot de passe, au chargement de la page.
 *
 * C'est ce qui rend le choix D29 vivable : le jeton d'accès meurt avec l'onglet,
 * mais le cookie de rafraîchissement survit, et un rechargement de page
 * redevient silencieux au lieu de renvoyer au formulaire de connexion.
 */
export async function resumeSession(): Promise<boolean> {
    return renew();
}

export async function signOut(): Promise<void> {
    try {
        await send('/api/token/revoke', { method: 'POST' }, true);
    } finally {
        // Le jeton local est oublié même si l'appel échoue : l'utilisateur a
        // demandé à partir, et le lui refuser parce que le réseau est coupé
        // serait absurde.
        accessToken = null;
    }
}

export async function fetchMe(): Promise<Me> {
    const response = await apiFetch('/api/me');

    if (!response.ok) {
        throw new Error(`/api/me a répondu ${response.status}`);
    }

    return (await response.json()) as Me;
}

/**
 * D32 — le serveur refuse tout tant qu'un mot de passe provisoire n'est pas
 * changé. Si cela arrive en cours de session (un administrateur vient de fixer
 * un provisoire alors que le jeton d'accès vivait encore), le SessionProvider
 * l'apprend par cet événement et relit `/api/me`, qui le dit.
 *
 * La réponse est CLONÉE pour être lue : l'appelant doit pouvoir lire la sienne.
 */
export const PASSWORD_CHANGE_EVENT = 'kelassi:password-change-required';

async function signalPasswordChangeRequired(response: Response): Promise<void> {
    if (!(response.headers.get('Content-Type') ?? '').includes('problem+json')) {
        return;
    }

    const problem = await ApiProblem.from(response.clone());

    if (problem.type === PASSWORD_CHANGE_REQUIRED || problem.type === TWO_FACTOR_SETUP_REQUIRED) {
        window.dispatchEvent(new Event(PASSWORD_CHANGE_EVENT));
    }
}

/**
 * Un appel métier qui rend du JSON, ou lève une ApiProblem. Les écrans ne
 * passent que par ici.
 */
export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await apiFetch(path, init);

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return (await response.json()) as T;
}

/**
 * D30 — changer son mot de passe coupe TOUTES les sessions, celle-ci comprise.
 * Le serveur efface le cookie ; le jeton en mémoire est oublié ici. L'appelant
 * reconduit à la connexion.
 */
export async function changeOwnPassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await apiFetch('/api/me/password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
    });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    accessToken = null;
}

/**
 * Les deux appels PUBLICS du mot de passe : sans jeton, sans cookie. Ils passent
 * par `send()` et non par `apiFetch()` — un 401 n'a ici rien à renouveler.
 */
export async function requestPasswordReset(email: string): Promise<void> {
    const response = await send('/api/password/forgot', { method: 'POST', body: JSON.stringify({ email }) }, false);

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await send('/api/password/reset', { method: 'POST', body: JSON.stringify({ token, newPassword }) }, false);

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }
}
